// AHA-47349-presenter-can-create-new-slide-type
// Developer slide types: fetch the slide types a user has created from the
// backend's public user-manifests endpoint and normalise them into the same
// shape /api/slide-types uses, so they can be appended to the marketplace
// catalogue (and listed on the Developer page).
import Config from '@/config'

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '')

// Bounded fetch: this runs on the cached picker-open fast path (awaited by every
// caller of fetchMarketplaceSlideTypes), so a slow/unreachable backend must never
// stall it indefinitely. Abort each request after a hard timeout; on abort fetch
// rejects and the caller falls back to [].
const FETCH_TIMEOUT_MS = 5000
const fetchWithTimeout = async (url, options) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null
  try {
    return await fetch(url, controller ? { ...options, signal: controller.signal } : options)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// Make a (possibly relative) URL absolute against the slide type's own baseUrl.
const absolutize = (base, url) => {
  const value = String(url || '')
  if (!value) return value
  if (/^https?:\/\//i.test(value)) return value
  return `${trimTrailingSlash(base)}${value.startsWith('/') ? '' : '/'}${value}`
}

// Normalise one user-manifest slide type into the SAME shape as an /api/slide-types
// catalogue entry, so every downstream consumer (picker, canvas/editor/audience
// resolution) treats it exactly like a first-party plugin slide type. The API entry
// mirrors the catalogue feed: a nested `ahaConfig` (URLs already absolute) plus the
// top-level entry (URLs relative). We flatten it the same way the marketplace loader
// does — spread `ahaConfig`, then the top-level entry so its keys win — then force
// the canonical fields. Key points:
//   • `type` IS the `pageId` — the stable per-deploy identifier a slide stores as
//     `slide.type`. embedApp resolves these by the `isDeveloperSlideType` marker,
//     and its marketplace branch explicitly excludes developer entries, so a
//     spread-in `source: 'market'` can never route one through the marketplace branch.
//   • ONLY the plugin's own iframe entries (canvas/editor/audience/handler) plus a
//     path-shaped `icon` are absolutized against the entry's `baseUrl`. A
//     FontAwesome-class `icon` (`fas fa-heart`, no `/`) and `previewUrl` (a
//     presenter-served asset) are kept verbatim.
//   • FAIL CLOSED: without an absolute base the iframe URL stays relative and would
//     resolve against the presenter origin (loading the app itself inside the
//     plugin iframe). Drop such entries, matching the old resolver's "no base => []".
//   • `plugin: true`; `setting` and the rest of the catalogue fields kept from the flatten.
const isAbsoluteUrl = (url) => /^https?:\/\//i.test(String(url || ''))
const normalize = (entry) => {
  if (!entry || typeof entry.pageId !== 'string' || !entry.pageId) return null
  const cfg = (entry.ahaConfig && typeof entry.ahaConfig === 'object') ? entry.ahaConfig : {}
  const { ahaConfig, ...topLevel } = entry
  const merged = { ...cfg, ...topLevel }
  const abs = (url) => absolutize(entry.baseUrl || '', url)
  const canvasUrl = abs(merged.canvasUrl)
  if (!isAbsoluteUrl(canvasUrl)) return null
  // Absolutize a path-shaped icon (`/icon.svg`) against the plugin host; keep a
  // FontAwesome class (no `/`) as-is.
  const icon = (typeof merged.icon === 'string' && merged.icon.includes('/')) ? abs(merged.icon) : merged.icon
  return {
    ...merged,
    type: entry.pageId,
    slug: entry.slug || cfg.slug || '',
    plugin: true,
    isDeveloperSlideType: true,
    canvasUrl,
    editorUrl: abs(merged.editorUrl),
    audienceUrl: abs(merged.audienceUrl),
    handlerUrl: merged.handlerUrl ? abs(merged.handlerUrl) : (merged.handlerUrl ?? null),
    icon,
    setting: (merged.setting && typeof merged.setting === 'object') ? merged.setting : {},
    baseUrl: entry.baseUrl || '',
    pageId: entry.pageId,
  }
}

/**
 * Returns the developer slide types a user has created, or [] when there is
 * nothing to add / the request fails. Read from the backend's public endpoint
 * (no auth — the userId is in the path), so it is safe to call from any build.
 */
export const fetchDeveloperSlideTypes = async ({ userId }) => {
  if (!userId) return []
  let data
  try {
    const url = `${trimTrailingSlash(Config.baseUrl)}/api/slide/user-manifests/public/${encodeURIComponent(userId)}`
    const res = await fetchWithTimeout(url, { headers: { accept: 'application/json' } })
    if (!res.ok) return []
    data = await res.json()
  } catch (e) {
    return []
  }
  // Guard a non-array `slideTypes` (object/string): `entries.map` would throw here,
  // OUTSIDE the fetch try/catch, and reject instead of returning [] as documented.
  const raw = Array.isArray(data) ? data : (data && data.slideTypes)
  const entries = Array.isArray(raw) ? raw : []
  return entries.map(normalize).filter(Boolean)
}

export default { fetchDeveloperSlideTypes }
